import { get } from "lodash";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { PageRequestError } from "components/PageRequestError";
import { CheckDuplicate } from "components/CheckDuplicate";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import useApp from "hooks/useApp";
import { useTranslation } from "react-i18next";
import useEditPage from "hooks/useEditPage";
const MetricsEditPage = (props) => {
  const app = useApp();
  // form validation schema
  const { t } = useTranslation();
  const UPDATE = t("UPDATE");
  const RESET = t("RESET");
  const EDIT_METRIC = t("EDIT_METRIC");
  const FACILITY = t("FACILITY");
  const MODULES = t("MODULES");
  const SELECT_FACILITY = t("SELECT_FACILITY");
  const STATUS = t("STATUS");
  const DETAILS = t("DETAILS");
  const METRIC_CONFIG = t("METRIC_CONFIG");
  const QUERY = t("QUERY");
  const POSITION = t("POSITION");
  const ALREADY_EXISTS = t("ALREADY_EXISTS");
  const CHECKING = t("CHECKING");
  const METRIC_TYPE = t("METRIC_TYPE");
  const NAME = t("NAME");
  const ENTER_NAME = t("ENTER_NAME");
  const ENTER_POSITION = t("ENTER_POSITION");
  const ENTER_STATUS = t("ENTER_STATUS");
  const ENTER_DETAILS = t("ENTER_DETAILS");
  const ENTER_QUERY = t("ENTER_QUERY");
  const ENTER_METRIC_CONFIG = t("ENTER_METRIC_CONFIG");
  const SELECT_METRIC_TYPE = t("SELECT_METRIC_TYPE");
  const validationSchema = yup.object().shape({
    org_code: yup.string().required(t("FACILITY_REQUIRED")),
    modules: yup
      .array()
      .min(1, t("ATLEAST_ONE_MODULE_SHOULDBE_SELECTED"))
      .required()
      .label("Modules"),
    name: yup.string().required(t("NAME_REQUIRED")),
    metric_type: yup.string().nullable().label("Metric Type"),
    position: yup.string().nullable().label("Position"),
    status: yup.string().nullable().label("Status"),
    details: yup.string().nullable().label("Details"),
    query: yup.string().nullable().label("Query"),
    metric_config: yup
      .string()
      .nullable()
      .test("is-json", t("INVALID_JSON_FORMAT"), (value) => {
        if (!value) return true; // Allow empty string
        try {
          JSON.parse(value);
          return true;
        } catch (e) {
          return false;
        }
      }),
  });
  // form default values
  const formDefaultValues = {
    org_code: "",
    modules: [],
    name: "",
    metric_type: "",
    position: "",
    status: "",
    details: "",
    query: "",
    metric_config: "",
  };
  //where page logics resides
  const pageController = useEditPage({ props, formDefaultValues, afterSubmit });
  //destructure and grab what we need
  const {
    formData,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    apiRequestError,
    inputClassName,
  } = pageController;
  //Event raised on form submit success
  function afterSubmit(response) {
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(`/metrics`);
    }
  }
  const handleReset = (formik) => {
    formik.resetForm({
      values: {
        ...formDefaultValues,
      },
    });
  };
  const getInputClassName = (error, touched) => {
    return error && touched ? "p-invalid" : "";
  };

  // loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }
  //display error page
  if (apiRequestError) {
    return <PageRequestError error={apiRequestError} />;
  }

  //page is ready when formdata loaded successfully
  if (pageReady) {
    return (
      <main id="MetricsEditPage" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={EDIT_METRIC}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-9 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                      submitForm(values);
                    }}
                  >
                    {(formik) => {
                      return (
                        <Form className={`${!props.isSubPage ? "card  " : ""}`}>
                          <div className="grid">
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">Id *</div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="id"
                                    disabled
                                    onChange={formik.handleChange}
                                    value={formik.values.id}
                                    label="Id"
                                    type="number"
                                    placeholder="Enter Id"
                                    min={0}
                                    step="any"
                                    className={inputClassName(
                                      formik?.errors?.id
                                    )}
                                  />
                                  <ErrorMessage
                                    name="id"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {FACILITY} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    name="org_code"
                                    value={formik.values.org_code || null}
                                    onChange={(e) => {
                                      const newValue =
                                        e.value === undefined ? "" : e.value;
                                      formik.setFieldValue(
                                        "org_code",
                                        newValue
                                      );
                                      formik.setFieldValue("modules", []);
                                      formik.setFieldTouched("org_code", false);
                                    }}
                                    options={get(props, "organisations", [])}
                                    optionLabel="name"
                                    optionValue="code"
                                    placeholder={SELECT_FACILITY}
                                    appendTo="self"
                                    style={{ width: "100%" }}
                                    className={`${getInputClassName(
                                      formik.errors.org_code,
                                      formik.touched.org_code
                                    )} w-full`}
                                    {...(formik.values.org_code && {
                                      showClear: true,
                                    })}
                                  />
                                  <ErrorMessage
                                    name="org_code"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {MODULES} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <MultiSelect
                                    value={formik.values.modules}
                                    onChange={(e) => {
                                      formik.setFieldValue("modules", e.value);
                                    }}
                                    options={props.modules.filter(
                                      (module) =>
                                        module.org_code ===
                                        formik.values.org_code
                                    )}
                                    optionLabel="module_name"
                                    optionValue="id"
                                    placeholder={
                                      formik.values.org_code?.length > 0
                                        ? "Select Modules"
                                        : "Select Facility First"
                                    }
                                    appendTo="self"
                                    maxSelectedLabels={3}
                                    className={`${getInputClassName(
                                      formik.errors.modules,
                                      formik.touched.modules
                                    )} w-full`}
                                    display="chip"
                                  />
                                  <ErrorMessage
                                    name="modules"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{NAME}*</div>
                                <div className="col-12 md:col-9">
                                  <CheckDuplicate
                                    value={formik.values.org_code}
                                    match={{
                                      key: "name",
                                      value: formik.values.name,
                                    }}
                                    unMatch={{
                                      key: "id",
                                      value: null,
                                    }}
                                    apiPath="metrics/get_metrics_by_org"
                                  >
                                    {(checker) => (
                                      <>
                                        <InputText
                                          name="name"
                                          onBlur={checker.check}
                                          onChange={formik.handleChange}
                                          value={formik.values.name}
                                          label="Name"
                                          type="text"
                                          placeholder={ENTER_NAME}
                                          className={`${getInputClassName(
                                            formik.errors.name,
                                            formik.touched.name
                                          )} w-full`}
                                        />
                                        <ErrorMessage
                                          name="name"
                                          component="span"
                                          className="p-error"
                                        />
                                        {!checker.loading && checker.exist && (
                                          <small className="p-error">
                                            {ALREADY_EXISTS}
                                          </small>
                                        )}
                                        {checker.loading && (
                                          <small className="text-500">
                                            {CHECKING}
                                          </small>
                                        )}
                                      </>
                                    )}
                                  </CheckDuplicate>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {METRIC_TYPE}
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    options={get(app, "metricTypes", [])}
                                    placeholder={SELECT_METRIC_TYPE}
                                    appendTo="self"
                                    className="w-full"
                                    value={formik.values.metric_type}
                                    optionValue="id"
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "metric_type",
                                        e.value
                                      );
                                    }}
                                    {...(formik.values.metric_type && {
                                      showClear: true,
                                    })}
                                  />
                                  <ErrorMessage
                                    name="metric_type"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {POSITION}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="position"
                                    onChange={formik.handleChange}
                                    value={formik.values.position}
                                    label="Position"
                                    type="text"
                                    placeholder={ENTER_POSITION}
                                    className={inputClassName(
                                      formik?.errors?.position
                                    )}
                                  />
                                  <ErrorMessage
                                    name="position"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{STATUS}</div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="status"
                                    onChange={formik.handleChange}
                                    value={formik.values.status}
                                    label={t("STATUS")}
                                    type="text"
                                    placeholder={ENTER_STATUS}
                                    className={inputClassName(
                                      formik?.errors?.status
                                    )}
                                  />
                                  <ErrorMessage
                                    name="status"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{DETAILS}</div>
                                <div className="col-12 md:col-9">
                                  <InputTextarea
                                    name="details"
                                    onChange={formik.handleChange}
                                    value={formik.values.details}
                                    label="Details"
                                    type="text"
                                    placeholder={ENTER_DETAILS}
                                    className={inputClassName(
                                      formik?.errors?.details
                                    )}
                                    rows={5}
                                  />
                                  <ErrorMessage
                                    name="details"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">{QUERY}</div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="query"
                                    onChange={formik.handleChange}
                                    value={formik.values.query}
                                    label="Query"
                                    type="text"
                                    placeholder={ENTER_QUERY}
                                    className={inputClassName(
                                      formik?.errors?.query
                                    )}
                                  />
                                  <ErrorMessage
                                    name="query"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {METRIC_CONFIG}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="metric_config"
                                    onChange={formik.handleChange}
                                    value={formik.values.metric_config}
                                    label="Metric Config"
                                    type="text"
                                    placeholder={ENTER_METRIC_CONFIG}
                                    className={inputClassName(
                                      formik?.errors?.metric_config
                                    )}
                                  />
                                  <ErrorMessage
                                    name="metric_config"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {props.showFooter && (
                            <div className="text-center mr-2">
                              <Button
                                onClick={(e) => handleSubmit(e, formik)}
                                type="submit"
                                label={UPDATE}
                                icon="pi pi-send"
                                loading={saving}
                                className=" mr-2"
                              />
                              <Button
                                onClick={() => handleReset(formik)}
                                className="p-button-secondary mr-2"
                                type="button"
                                label={RESET}
                                icon="pi pi-refresh"
                              />
                            </div>
                          )}
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};
const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "metrics",
      getApiPath: "metrics/get_metrics",
      putApiPath: "metrics/edit_metrics",
      routeName: "metricsedit",
      submitButtonLabel: "Update",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("UPDATE_METRIC"),
      msgAfterSave: t("METRIC_UPDATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: false,
    };
    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(MetricsEditPage);
